import type { Replayer } from "@rrweb/replay";
import { sessionVocabulary } from "@/features/session-diagnostics/api/session-diagnostics.api";
import { MAX_ASSET_BYTES, MAX_ASSET_NODES, MAX_SCENE_ASSET_BYTES, svgProjection } from "@/features/session-diagnostics/player/svgProjection";

// Every payload is treated as hostile (D10). The sandbox stops scripts, forms and popups;
// this pass stops the rest: anything that would make the frame FETCH (images, frames,
// stylesheets, fonts, media) or navigate never reaches the player.

export type ReplayEvent = Exclude<Parameters<Replayer["addEvent"]>[0], string>;

export type ReplayerHandle = {
    addEvent: (event: ReplayEvent) => void;
    destroy: () => void;
    pause?: () => void;
    resume?: () => void;
    // Recorded playback only: jumps to a recorder timestamp inside the fragment; false when
    // the instant is outside it (the caller tells the operator).
    seek?: (timestamp: number) => boolean;
    // Recorded playback only (live is paced by the recorder).
    setSpeed?: (speed: ReplaySpeed) => void;
    setSkipIdle?: (skipIdle: boolean) => void;
    // The recorder timestamp the scene shows now.
    currentTime?: () => number;
};

export type ReplaySpeed = 1 | 2 | 4;

// `null` = the player refused to start (e.g. its frame is not inert); nothing is shown.
// `liveHead` = the latest recorder timestamp already buffered for the scene (see liveHeadOf).
export type ReplayPointer = { x: number; y: number; kind: "Toque" | "Clic" };
export type PlaybackOptions = {
    recordedEvents?: ReplayEvent[];
    // `null` = the marker's moment is over (the player held the scene on it until now).
    onPointer?: (pointer: ReplayPointer | null) => void;
    onViewport?: (viewport: { width: number; height: number }) => void;
    onFinish?: () => void;
    onError?: (error: SessionError) => void;
    speed?: ReplaySpeed;
    skipIdle?: boolean;
};
export type ReplayerFactory = (root: HTMLElement, liveHead: number, options?: PlaybackOptions) => ReplayerHandle | null;

// @rrweb/replay 2.1.6 creates its frame with exactly this sandbox and refuses to rebuild
// into any other (replay.js:4348 createSandboxedIframe, :3963-3970 isSupportedSandboxedIframe).
export const REPLAY_SANDBOX_TOKENS: readonly string[] = ["allow-same-origin"];

const isInertFrame = (iframe: HTMLIFrameElement): boolean => {
    const attribute = iframe.getAttribute("sandbox");
    if (attribute === null) return false;
    const tokens = attribute.split(/\s+/).filter((token) => token !== "");
    return tokens.length === REPLAY_SANDBOX_TOKENS.length
        && REPLAY_SANDBOX_TOKENS.every((token) => tokens.includes(token));
};

const EVENT_FULL_SNAPSHOT = 2;
const EVENT_INCREMENTAL = 3;
const EVENT_META = 4;
const EVENT_CUSTOM = 5;

const SOURCE_MUTATION = 0;
const SOURCE_STYLE_SHEET_RULE = 8;
const SOURCE_STYLE_DECLARATION = 13;
const SOURCE_ADOPTED_STYLE_SHEET = 15;
// Mouse, scroll, viewport, input, touch, selection, drag: positions and masked values.
// Media (7), canvas (9), font (10: the player loads a FontFace from its source), log (11)
// and custom-element (16) sources are dropped.
const PASSIVE_SOURCES = new Set([1, 2, 3, 4, 5, 6, 12, 14]);

const NODE_ELEMENT = 2;
const NODE_TEXT = 3;

// The capture allowlist (D8) plus what the replay needs to draw it: masked values and
// the player's own size hints. `id`, `href`, `src`, `srcset`, `srcdoc`, `action`, `on*`,
// `http-equiv`, `content` and every other attribute are dropped.
const ALLOWED_ATTRIBUTES = new Set(["class", "role", "type", "aria-hidden", "disabled", "hidden", "dir", "style", "value", "checked", "selected", "rr_width", "rr_height", "width", "height"]);
const BOOLEAN_ATTRIBUTES = new Set(["disabled", "hidden", "checked", "selected"]);
const SIZE_VALUE = /^\d{1,5}(\.\d{1,3})?px$/;
const TAG_NAME = /^[a-z][a-z0-9-]{0,63}$/;

export const PLACEHOLDER_ATTRIBUTE = "data-replay-placeholder";
export const PLACEHOLDER_LABELS = {
    image: "",
    media: "Contenido multimedia no reproducido",
    frame: "Marco externo no reproducido",
    canvas: "Canvas no reproducido",
    blocked: "Contenido bloqueado",
} as const;
type PlaceholderKind = keyof typeof PLACEHOLDER_LABELS;

const PLACEHOLDER_TAGS: Record<string, PlaceholderKind> = {
    img: "image",
    picture: "image",
    image: "image",
    svg: "image",
    video: "media",
    audio: "media",
    source: "media",
    track: "media",
    iframe: "frame",
    frame: "frame",
    frameset: "frame",
    object: "frame",
    embed: "frame",
    portal: "frame",
    canvas: "canvas",
    script: "blocked",
    noscript: "blocked",
    base: "blocked",
    meta: "blocked",
    template: "blocked",
    slot: "blocked",
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const decodeCssEscapes = (css: string): string =>
    css.replace(/\\([0-9a-fA-F]{1,6})\s?|\\([\s\S])/g, (_match, hex: string | undefined, char: string | undefined) =>
        hex !== undefined ? String.fromCodePoint(Math.min(Number.parseInt(hex, 16), 0x10ffff)) : char ?? "");

const FETCHING_CSS = /(url|image-set|image|cross-fade|element|src|expression|paint)\s*\(|@import|@namespace|behavior\s*:|-moz-binding/i;

// The app's own main stylesheet is ~272 000 characters (dev segment f1767232); anything
// past four times that is refused whole rather than scanned.
export const MAX_CSS_CHARS = 1024 * 1024;

// Every `@import …;` removed and every `url(…)` replaced by `none`, in ONE left-to-right
// pass (a regex with a lazy body backtracks quadratically on `url("url("…`). Anything this
// pass cannot close — an unterminated string or a missing `)` — is refused (null).
// Each search resumes only once `at` has passed its last hit: re-searching both on every
// step rescans the whole rest for the absent one (`url` × 340 000 took 2.8 s).
const stripFetches = (css: string): string | null => {
    const lower = css.toLowerCase();
    let out = "";
    let at = 0;
    let importAt = lower.indexOf("@import");
    let urlAt = lower.indexOf("url");
    for (;;) {
        if (importAt !== -1 && importAt < at) importAt = lower.indexOf("@import", at);
        if (urlAt !== -1 && urlAt < at) urlAt = lower.indexOf("url", at);
        if (importAt === -1 && urlAt === -1) return out + css.slice(at);
        if (importAt !== -1 && (urlAt === -1 || importAt < urlAt)) {
            const end = css.indexOf(";", importAt);
            out += css.slice(at, importAt);
            if (end === -1) return out;
            at = end + 1;
            continue;
        }
        let open = urlAt + 3;
        while (open < css.length && /\s/.test(css.charAt(open))) open += 1;
        if (css.charAt(open) !== "(") {
            out += css.slice(at, urlAt + 3);
            at = urlAt + 3;
            continue;
        }
        let close = open + 1;
        while (close < css.length && /\s/.test(css.charAt(close))) close += 1;
        const quote = css.charAt(close);
        if (quote === "\"" || quote === "'") {
            const end = css.indexOf(quote, close + 1);
            if (end === -1) return null;
            close = end + 1;
            while (close < css.length && /\s/.test(css.charAt(close))) close += 1;
            if (css.charAt(close) !== ")") return null;
        } else {
            close = css.indexOf(")", open);
            if (close === -1) return null;
        }
        out += `${css.slice(at, urlAt)}none`;
        at = close + 1;
    }
};

// Any CSS a stylesheet, a style attribute or a text node could carry. A value that still
// names a fetching construct after unescaping is dropped whole (fail closed). Newlines are
// normalised FIRST, as the browser does before tokenising: a hex escape ends at ONE
// whitespace and CRLF is one (`\75\r\nrl(` is `url(`), so decoding the raw text would leave
// a stray `\n` that hides the construct.
const sanitizeCss = (raw: string): string => {
    if (raw.length > MAX_CSS_CHARS) return "";
    const css = raw.replace(/\r\n?|\f/g, "\n");
    const stripped = stripFetches(css);
    if (stripped === null) return "";
    return FETCHING_CSS.test(decodeCssEscapes(stripped)) ? "" : stripped;
};

const sanitizeAttributeValue = (name: string, value: unknown): string | null => {
    if (typeof value !== "string") return null;
    if (name === "style") return sanitizeCss(value);
    if (name === "width" || name === "height") return /^\d{1,4}(\.\d{1,3})?(px|%)?$/.test(value) ? value : null;
    if (name === "rr_width" || name === "rr_height") return SIZE_VALUE.test(value) ? value : null;
    return value;
};

const isAllowedAttribute = (name: string): boolean =>
    ALLOWED_ATTRIBUTES.has(name) || /^aria-[a-z-]{1,40}$/.test(name) || /^data-replay-[a-z-]{1,40}$/.test(name);

const sanitizeAttributes = (attributes: unknown): Record<string, string | boolean> => {
    const out: Record<string, string | boolean> = {};
    if (isRecord(attributes) === false) return out;
    for (const [name, value] of Object.entries(attributes)) {
        const lower = name.toLowerCase();
        if (isAllowedAttribute(lower) === false || lower === PLACEHOLDER_ATTRIBUTE) continue;
        if (value === true && BOOLEAN_ATTRIBUTES.has(lower)) {
            out[lower] = true;
            continue;
        }
        const safe = sanitizeAttributeValue(lower, value);
        if (safe !== null) out[lower] = safe;
    }
    return out;
};

// The stylesheet ids of the scene on screen. A browser parses a <style> as the JOINED text
// of its children, so CSS is only ever cleaned whole: at the full snapshot each <style>
// is collapsed into one text node, and afterwards nothing may add to, edit or re-attribute
// a tracked stylesheet — those changes are DROPPED (fail closed), never cleaned piecewise.
// Marked <svg> assets are tracked too: every id of a projected subtree points at the
// cumulative budget of its root, which is poisoned (and the root removed) once exceeded.
// The scene budget is LIVE: what a root or an image charged is refunded when it leaves.
// `parents` knows every node of the scene, so a removal forgets its whole subtree and an
// add that re-uses a known id is dropped (a re-add never resets a budget).
export type AssetBudget = { rootId: number; parentId: number | null; nodes: number; bytes: number; charged: number; poisoned: boolean };
export type ImageCost = { bytes: number; pixels: number };

export type StyleTracker = {
    nodes: ReadonlySet<number>;
    texts: ReadonlySet<number>;
    assets: ReadonlyMap<number, Readonly<AssetBudget>>;
    images: ReadonlyMap<number, Readonly<ImageCost>>;
    parents: ReadonlyMap<number, number | null>;
    sceneBytes: number;
    scenePixels: number;
};

type TrackerDraft = {
    nodes: Set<number>;
    texts: Set<number>;
    assets: Map<number, AssetBudget>;
    images: Map<number, Readonly<ImageCost>>;
    parents: Map<number, number | null>;
    // `parents` inverted, kept in step with it: a removal walks only its own subtree.
    children: Map<number, Set<number>>;
    sceneBytes: number;
    scenePixels: number;
};

const EMPTY_TRACKER: StyleTracker = {
    nodes: new Set<number>(),
    texts: new Set<number>(),
    assets: new Map<number, AssetBudget>(),
    images: new Map<number, ImageCost>(),
    parents: new Map<number, number | null>(),
    sceneBytes: 0,
    scenePixels: 0,
};

const NO_COST: Readonly<ImageCost> = { bytes: 0, pixels: 0 };

// Budgets are shared by every id of a root, so the copy keeps that sharing.
const draftOf = (tracker: StyleTracker): TrackerDraft => {
    const clones = new Map<Readonly<AssetBudget>, AssetBudget>();
    const assets = new Map<number, AssetBudget>();
    tracker.assets.forEach((budget, id) => {
        const clone = clones.get(budget) ?? { ...budget };
        clones.set(budget, clone);
        assets.set(id, clone);
    });
    const children = new Map<number, Set<number>>();
    tracker.parents.forEach((parent, child) => {
        if (parent === null) return;
        const siblings = children.get(parent);
        if (siblings === undefined) children.set(parent, new Set([child]));
        else siblings.add(child);
    });
    return {
        nodes: new Set(tracker.nodes),
        texts: new Set(tracker.texts),
        assets,
        images: new Map(tracker.images),
        parents: new Map(tracker.parents),
        children,
        sceneBytes: tracker.sceneBytes,
        scenePixels: tracker.scenePixels,
    };
};

const isStyleTag = (tagName: string): boolean => tagName === "style" || tagName === "link";

const tagOf = (node: Record<string, unknown>): string => (typeof node.tagName === "string" ? node.tagName.toLowerCase() : "");

const childrenOf = (node: Record<string, unknown>): unknown[] => (Array.isArray(node.childNodes) ? node.childNodes : []);

// Rebuilding and cleaning a tree recurse over it: a hostile snapshot nested 10 000 deep
// would overflow the stack and break the whole poll. The app's real scenes are ≤ 24 deep
// and ≤ 425 nodes per snapshot (dev segments 25d688b9, f1767232); past these bounds the
// tree is dropped whole, measured without recursion before anything else reads it.
export const MAX_TREE_DEPTH = 256;
export const MAX_TREE_NODES = 50_000;
const fitsTree = (root: unknown): boolean => {
    const stack: { node: unknown; depth: number }[] = [{ node: root, depth: 1 }];
    let count = 0;
    for (let next = stack.pop(); next !== undefined; next = stack.pop()) {
        count += 1;
        if (next.depth > MAX_TREE_DEPTH || count > MAX_TREE_NODES) return false;
        if (isRecord(next.node) === false) continue;
        for (const child of childrenOf(next.node)) stack.push({ node: child, depth: next.depth + 1 });
    }
    return true;
};

const collectIds = (node: unknown, into: Set<number>): void => {
    if (isRecord(node) === false) return;
    if (typeof node.id === "number") into.add(node.id);
    childrenOf(node).forEach((child) => collectIds(child, into));
};

// Registers every stylesheet in a raw subtree before anything in the same batch is kept,
// so an add that targets a stylesheet created later in the same batch is still dropped.
const registerStyles = (node: unknown, draft: TrackerDraft): void => {
    if (isRecord(node) === false) return;
    if (node.type === NODE_ELEMENT && isStyleTag(tagOf(node)) && typeof node.id === "number") {
        draft.nodes.add(node.id);
        childrenOf(node).forEach((child) => collectIds(child, draft.texts));
        return;
    }
    childrenOf(node).forEach((child) => registerStyles(child, draft));
};

// The CSS a <style> applies is its own `_cssText` or else the concatenation of its direct
// text children — cleaned as ONE string, which becomes its ONLY child.
const collapseStylesheet = (node: Record<string, unknown>, id: number, tagName: string): Record<string, unknown> => {
    const cssText = isRecord(node.attributes) && typeof node.attributes._cssText === "string" ? node.attributes._cssText : null;
    const textChildren = childrenOf(node).filter(isRecord).filter((child) => child.type === NODE_TEXT && typeof child.id === "number");
    if (tagName === "link" && cssText === null) {
        return { type: NODE_ELEMENT, id, tagName: "div", attributes: {}, childNodes: [] };
    }
    const joined = cssText ?? textChildren.map((child) => (typeof child.textContent === "string" ? child.textContent : "")).join("");
    const first = textChildren[0];
    const css = sanitizeCss(joined);
    if (first === undefined || typeof first.id !== "number") {
        return { type: NODE_ELEMENT, id, tagName: "style", attributes: css === "" ? {} : { _cssText: css }, childNodes: [] };
    }
    return { type: NODE_ELEMENT, id, tagName: "style", attributes: {}, childNodes: [{ type: NODE_TEXT, id: first.id, textContent: css, isStyle: true }] };
};

const NODE_DOCUMENT = 0;
const NODE_DOCUMENT_TYPE = 1;
const NODE_COMMENT = 5;

const setParent = (draft: TrackerDraft, id: number, parentId: number | null): void => {
    const previous = draft.parents.get(id);
    if (previous !== undefined && previous !== null) draft.children.get(previous)?.delete(id);
    draft.parents.set(id, parentId);
    if (parentId === null) return;
    const siblings = draft.children.get(parentId);
    if (siblings === undefined) draft.children.set(parentId, new Set([id]));
    else siblings.add(id);
};

const hasOwn = (record: unknown, key: string): boolean => isRecord(record) && Object.prototype.hasOwnProperty.call(record, key);

const chargeScene = (draft: TrackerDraft, bytes: number, pixels: number): boolean => {
    if (draft.sceneBytes + bytes > MAX_SCENE_ASSET_BYTES || draft.scenePixels + pixels > MAX_SCENE_PIXELS) return false;
    draft.sceneBytes += bytes;
    draft.scenePixels += pixels;
    return true;
};

const refundScene = (draft: TrackerDraft, bytes: number, pixels: number): void => {
    draft.sceneBytes -= bytes;
    draft.scenePixels -= pixels;
};

// A projected root owns a budget shared by every id of its subtree. A root that does not
// project, or does not fit, is registered POISONED so nothing later grows under it.
const registerAsset = (raw: Record<string, unknown>, rootId: number, parentId: number | null, draft: TrackerDraft): Record<string, unknown> | null => {
    const projected = svgProjection.root(raw);
    const weight = projected === null ? null : svgProjection.weigh(projected);
    const fits = projected !== null && weight !== null && chargeScene(draft, weight.bytes, 0);
    const bytes = weight === null ? 0 : weight.bytes;
    const budget: AssetBudget = { rootId, parentId, nodes: weight === null ? 0 : weight.nodes, bytes, charged: fits ? bytes : 0, poisoned: fits === false };
    draft.assets.set(rootId, budget);
    if (fits === false || projected === null) return null;
    svgProjection.collectIds(projected).forEach((id) => draft.assets.set(id, budget));
    return projected;
};

const DATA_IMAGE = /^data:image\/(png|jpeg|webp|svg\+xml);base64,([A-Za-z0-9+/]+={0,2})$/;
const MAX_RASTER_BASE64 = 24_000;
const MAX_IMAGE_SIDE = 1_024;
const MAX_IMAGE_PIXELS = 1_024 * 1_024;
const MAX_SCENE_PIXELS = 4 * 1_024 * 1_024;
// Base64 of MAX_ASSET_BYTES: the decoded SVG is then held to the per-root budgets.
const MAX_SVG_BASE64 = 4 * Math.ceil(MAX_ASSET_BYTES / 3);

const decodeBase64Utf8 = (base64: string): string | null => {
    try {
        const binary = atob(base64);
        return decodeURIComponent(Array.from(binary, (char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`).join(""));
    } catch {
        return null;
    }
};

const encodeBase64Utf8 = (text: string): string =>
    btoa(encodeURIComponent(text).replace(/%([0-9A-F]{2})/g, (_match, hex: string) => String.fromCharCode(Number.parseInt(hex, 16))));

// The received SVG is never used: it is parsed off-DOM, projected to the asset grammar and
// re-serialized, so only what the grammar allows can reach the frame.
const reprojectSvg = (base64: string): string | null => {
    const text = decodeBase64Utf8(base64);
    if (text === null || /<!(DOCTYPE|ENTITY)/i.test(text) || typeof DOMParser === "undefined") return null;
    const parsed = svgProjection.fromDocument(new DOMParser().parseFromString(text, "image/svg+xml"));
    const projected = parsed === null ? null : svgProjection.root(parsed);
    return projected === null ? null : `data:image/svg+xml;base64,${encodeBase64Utf8(svgProjection.serialize(projected))}`;
};

const decodeBase64Bytes = (base64: string): Uint8Array | null => {
    try {
        return Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
    } catch {
        return null;
    }
};

type Size = { width: number; height: number };
const ascii = (bytes: Uint8Array, at: number, length: number): string =>
    Array.from(bytes.subarray(at, at + length), (byte) => String.fromCharCode(byte)).join("");
const u16be = (bytes: Uint8Array, at: number): number => bytes[at] * 256 + bytes[at + 1];
const u16le = (bytes: Uint8Array, at: number): number => bytes[at] + bytes[at + 1] * 256;
const u24le = (bytes: Uint8Array, at: number): number => bytes[at] + bytes[at + 1] * 256 + bytes[at + 2] * 65_536;
const u32be = (bytes: Uint8Array, at: number): number => u16be(bytes, at) * 65_536 + u16be(bytes, at + 2);

const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10];
const pngSize = (bytes: Uint8Array): Size | null =>
    bytes.length >= 24 && PNG_SIGNATURE.every((byte, index) => bytes[index] === byte) && ascii(bytes, 12, 4) === "IHDR"
        ? { width: u32be(bytes, 16), height: u32be(bytes, 20) }
        : null;

const JPEG_SOF = new Set([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf]);
const jpegSize = (bytes: Uint8Array): Size | null => {
    if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
    let at = 2;
    while (at + 3 < bytes.length) {
        if (bytes[at] !== 0xff) return null;
        const marker = bytes[at + 1];
        if (marker === 0xff) {
            at += 1;
            continue;
        }
        if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) {
            at += 2;
            continue;
        }
        if (marker === 0xda || marker === 0xd9) return null;
        if (JPEG_SOF.has(marker)) return at + 8 < bytes.length ? { width: u16be(bytes, at + 7), height: u16be(bytes, at + 5) } : null;
        const length = u16be(bytes, at + 2);
        if (length < 2) return null;
        at += 2 + length;
    }
    return null;
};

const webpSize = (bytes: Uint8Array): Size | null => {
    if (bytes.length < 30 || ascii(bytes, 0, 4) !== "RIFF" || ascii(bytes, 8, 4) !== "WEBP") return null;
    const chunk = ascii(bytes, 12, 4);
    if (chunk === "VP8 ") {
        return bytes[23] === 0x9d && bytes[24] === 0x01 && bytes[25] === 0x2a
            ? { width: u16le(bytes, 26) % 16_384, height: u16le(bytes, 28) % 16_384 }
            : null;
    }
    if (chunk === "VP8L") {
        if (bytes[20] !== 0x2f) return null;
        return {
            width: 1 + bytes[21] + (bytes[22] % 64) * 256,
            height: 1 + Math.floor(bytes[22] / 64) + bytes[23] * 4 + (bytes[24] % 16) * 1_024,
        };
    }
    return chunk === "VP8X" ? { width: 1 + u24le(bytes, 24), height: 1 + u24le(bytes, 27) } : null;
};

const RASTER_SIZE: Record<string, (bytes: Uint8Array) => Size | null> = { png: pngSize, jpeg: jpegSize, webp: webpSize };

// The decoder allocates by the DECLARED dimensions, not by the encoded length: a raster is
// kept only when its own header parses and declares at most 1 024×1 024 (1 M pixels).
const rasterPixels = (kind: string, payload: string): number | null => {
    const bytes = decodeBase64Bytes(payload);
    const measure = Object.prototype.hasOwnProperty.call(RASTER_SIZE, kind) ? RASTER_SIZE[kind] : undefined;
    const size = bytes === null || measure === undefined ? null : measure(bytes);
    if (size === null || size.width < 1 || size.height < 1 || size.width > MAX_IMAGE_SIDE || size.height > MAX_IMAGE_SIDE) return null;
    const pixels = size.width * size.height;
    return pixels <= MAX_IMAGE_PIXELS ? pixels : null;
};

const dataImageOf = (value: unknown): { src: string; pixels: number } | null => {
    if (typeof value !== "string") return null;
    const match = DATA_IMAGE.exec(value);
    if (match === null) return null;
    const [, kind, payload] = match;
    if (kind !== "svg+xml") {
        const pixels = payload.length <= MAX_RASTER_BASE64 ? rasterPixels(kind, payload) : null;
        return pixels === null ? null : { src: value, pixels };
    }
    const src = payload.length <= MAX_SVG_BASE64 ? reprojectSvg(payload) : null;
    return src === null ? null : { src, pixels: 0 };
};

const sanitizeNode = (node: unknown, draft: TrackerDraft, parentId: number | null): Record<string, unknown> | null => {
    if (isRecord(node) === false || typeof node.id !== "number" || Number.isSafeInteger(node.id) === false || node.id < 0 || typeof node.type !== "number") return null;
    const id = node.id;
    if (node.type === NODE_TEXT) {
        // Outside a stylesheet text is never parsed as CSS; it is still cleaned so no
        // address survives in it.
        const text = typeof node.textContent === "string" ? sanitizeCss(node.textContent) : "";
        return { type: NODE_TEXT, id, textContent: text };
    }
    if (node.type === NODE_DOCUMENT) {
        return { type: NODE_DOCUMENT, id, childNodes: childrenOf(node).map((child) => sanitizeNode(child, draft, id)).filter(isKept) };
    }
    if (node.type === NODE_DOCUMENT_TYPE) return { type: NODE_DOCUMENT_TYPE, id, name: "html", publicId: "", systemId: "" };
    if (node.type === NODE_COMMENT) return { type: NODE_COMMENT, id, textContent: "" };
    if (node.type !== NODE_ELEMENT) return null;
    const tagName = tagOf(node);
    const attributes = sanitizeAttributes(node.attributes);
    if (svgProjection.isRoot(node)) {
        const asset = registerAsset(node, id, parentId, draft);
        if (asset !== null) return asset;
    }
    if (tagName === "img" && hasOwn(node.attributes, "data-replay-asset") && isRecord(node.attributes)) {
        // The only path by which `src` enters the replay DOM. Without an acceptable one the
        // img stays empty (no request) and registered, so a later data:image can still arrive.
        const image = dataImageOf(node.attributes.src);
        const kept = image !== null && chargeScene(draft, image.src.length, image.pixels);
        draft.images.set(id, kept ? { bytes: image.src.length, pixels: image.pixels } : NO_COST);
        return { type: NODE_ELEMENT, id, tagName: "img", attributes: kept ? { ...attributes, src: image.src } : attributes, childNodes: [] };
    }
    const placeholder: PlaceholderKind | null = TAG_NAME.test(tagName) === false
        ? "blocked"
        : Object.prototype.hasOwnProperty.call(PLACEHOLDER_TAGS, tagName) ? PLACEHOLDER_TAGS[tagName] : null;
    if (placeholder !== null) {
        const size: Record<string, string> = {};
        const width = attributes.rr_width;
        const height = attributes.rr_height;
        if (typeof width === "string") size.rr_width = width;
        if (typeof height === "string") size.rr_height = height;
        return {
            type: NODE_ELEMENT,
            id,
            tagName: "div",
            attributes: { ...attributes, ...size, "aria-hidden": "true", [PLACEHOLDER_ATTRIBUTE]: PLACEHOLDER_LABELS[placeholder] },
            childNodes: [],
        };
    }
    if (isStyleTag(tagName)) return collapseStylesheet(node, id, tagName);
    return {
        type: NODE_ELEMENT,
        id,
        // A form is only a container here: no action, and the sandbox forbids submission.
        tagName: tagName === "form" ? "div" : tagName,
        attributes,
        childNodes: childrenOf(node).map((child) => sanitizeNode(child, draft, id)).filter(isKept),
        ...(node.isSVG === true ? { isSVG: true } : {}),
    };
};

const isKept = (node: Record<string, unknown> | null): node is Record<string, unknown> => node !== null;

// A style ATTRIBUTE and each property of a declaration are parsed on their own (never
// concatenated with anything), so each is cleaned whole on its own.
const sanitizeStyleAttribute = (value: unknown): unknown => {
    if (typeof value === "string") return sanitizeCss(value);
    if (isRecord(value) === false) return null;
    const out: Record<string, unknown> = {};
    for (const [property, entry] of Object.entries(value)) {
        if (entry === false) out[property] = false;
        else if (typeof entry === "string") out[property] = sanitizeCss(entry);
        else if (Array.isArray(entry) && typeof entry[0] === "string") out[property] = [sanitizeCss(entry[0]), typeof entry[1] === "string" ? entry[1] : ""];
    }
    return out;
};

// Recorded node ids are non-negative safe integers; anything else addresses nothing.
const isTracked = (value: unknown, draft: TrackerDraft): boolean =>
    typeof value !== "number" || Number.isSafeInteger(value) === false || value < 0
    || draft.nodes.has(value) || draft.texts.has(value);

const isNodeId = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

// Every add that belongs under an asset in this batch, whatever order it is listed in: one
// listed before its asset parent is DROPPED (fail closed), never sanitized as plain DOM.
const assetBoundIds = (adds: Record<string, unknown>[], draft: TrackerDraft): Set<number> => {
    const byParent = new Map<number, number[]>();
    const bound = new Set<number>();
    const queue: number[] = [];
    for (const add of adds) {
        if (isRecord(add.node) === false || isNodeId(add.node.id) === false || isNodeId(add.parentId) === false) continue;
        const siblings = byParent.get(add.parentId);
        if (siblings === undefined) byParent.set(add.parentId, [add.node.id]);
        else siblings.push(add.node.id);
        if (svgProjection.isRoot(add.node)) queue.push(add.node.id);
    }
    byParent.forEach((_children, parentId) => { if (draft.assets.has(parentId)) queue.push(parentId); });
    for (let next = queue.pop(); next !== undefined; next = queue.pop()) {
        for (const childId of byParent.get(next) ?? []) {
            if (bound.has(childId)) continue;
            bound.add(childId);
            queue.push(childId);
        }
    }
    return bound;
};

const poison = (budget: AssetBudget, draft: TrackerDraft, removes: Record<string, unknown>[]): void => {
    if (budget.poisoned) return;
    budget.poisoned = true;
    refundScene(draft, budget.charged, 0);
    budget.charged = 0;
    if (budget.parentId !== null) removes.push({ parentId: budget.parentId, id: budget.rootId });
};

// Charges an asset change to its root; false = the change is dropped (root poisoned).
const chargeAsset = (budget: AssetBudget, nodes: number, bytes: number, draft: TrackerDraft, removes: Record<string, unknown>[]): boolean => {
    if (budget.poisoned) return false;
    if (budget.nodes + nodes > MAX_ASSET_NODES || budget.bytes + bytes > MAX_ASSET_BYTES || chargeScene(draft, bytes, 0) === false) {
        poison(budget, draft, removes);
        return false;
    }
    budget.nodes += nodes;
    budget.bytes += bytes;
    budget.charged += bytes;
    return true;
};

const valueBytes = (attributes: Record<string, string>): number => Object.values(attributes).reduce((sum, value) => sum + value.length, 0);

const sanitizeAssetAdd = (add: Record<string, unknown>, parentId: number, draft: TrackerDraft, removes: Record<string, unknown>[]): Record<string, unknown>[] => {
    const budget = draft.assets.get(parentId);
    if (budget === undefined || isRecord(add.node) === false || isNodeId(add.node.id) === false) return [];
    draft.assets.set(add.node.id, budget);
    setParent(draft, add.node.id, parentId);
    const node = svgProjection.child(add.node);
    if (node === null || chargeAsset(budget, 1, valueBytes(svgProjection.attributes(node.attributes)), draft, removes) === false) return [];
    return [{ parentId, nextId: add.nextId === undefined ? null : add.nextId, node }];
};

const sanitizeAttributeChange = (change: Record<string, unknown>, draft: TrackerDraft, removes: Record<string, unknown>[]): Record<string, unknown> | null => {
    const id = change.id;
    if (isTracked(id, draft) || isNodeId(id) === false) return null;
    const budget = draft.assets.get(id);
    if (budget !== undefined) {
        const projected = svgProjection.attributes(change.attributes);
        return chargeAsset(budget, 0, valueBytes(projected), draft, removes) ? { id, attributes: projected } : null;
    }
    const out: Record<string, unknown> = {};
    if (isRecord(change.attributes)) {
        for (const [name, value] of Object.entries(change.attributes)) {
            const lower = name.toLowerCase();
            if (isAllowedAttribute(lower) === false || lower === PLACEHOLDER_ATTRIBUTE) continue;
            if (lower === "style") out[lower] = sanitizeStyleAttribute(value);
            else if (value === null) out[lower] = null;
            else {
                const safe = sanitizeAttributeValue(lower, value);
                if (safe !== null) out[lower] = safe;
            }
        }
        const previous = draft.images.get(id);
        if (previous !== undefined && hasOwn(change.attributes, "src")) {
            // `null` removes the image (the app switched to a source that is not replayed).
            const image = change.attributes.src === null ? null : dataImageOf(change.attributes.src);
            refundScene(draft, previous.bytes, previous.pixels);
            if (change.attributes.src === null) {
                draft.images.set(id, NO_COST);
                out.src = null;
            } else if (image !== null && chargeScene(draft, image.src.length, image.pixels)) {
                draft.images.set(id, { bytes: image.src.length, pixels: image.pixels });
                out.src = image.src;
            } else chargeScene(draft, previous.bytes, previous.pixels);
        }
    }
    return { id, attributes: out };
};

const linkTree = (node: unknown, parentId: number | null, draft: TrackerDraft): void => {
    if (isRecord(node) === false || isNodeId(node.id) === false) return;
    setParent(draft, node.id, parentId);
    const id = node.id;
    childrenOf(node).forEach((child) => linkTree(child, id, draft));
};

const isKnown = (id: number, draft: TrackerDraft): boolean =>
    draft.parents.has(id) || draft.assets.has(id) || draft.images.has(id) || draft.nodes.has(id) || draft.texts.has(id);

// A removal takes its whole subtree off the screen: every id in it is forgotten and what
// its asset roots and images charged to the scene is refunded.
const forgetRemoved = (removedIds: number[], draft: TrackerDraft): void => {
    const seen = new Set<number>();
    const stack = [...removedIds];
    for (let id = stack.pop(); id !== undefined; id = stack.pop()) {
        if (seen.has(id)) continue;
        seen.add(id);
        const budget = draft.assets.get(id);
        if (budget !== undefined && budget.rootId === id) {
            refundScene(draft, budget.charged, 0);
            budget.charged = 0;
        }
        const image = draft.images.get(id);
        if (image !== undefined) refundScene(draft, image.bytes, image.pixels);
        draft.assets.delete(id);
        draft.images.delete(id);
        draft.nodes.delete(id);
        draft.texts.delete(id);
        const parent = draft.parents.get(id);
        if (parent !== undefined && parent !== null) draft.children.get(parent)?.delete(id);
        draft.parents.delete(id);
        draft.children.get(id)?.forEach((child) => stack.push(child));
        draft.children.delete(id);
    }
};

const sanitizeMutation = (data: Record<string, unknown>, draft: TrackerDraft): Record<string, unknown> => {
    const rawAdds = Array.isArray(data.adds) ? data.adds.filter(isRecord).filter((add) => fitsTree(add.node)) : [];
    // The player applies removes, then adds, then texts and attributes: same order here.
    const removes: Record<string, unknown>[] = Array.isArray(data.removes)
        ? data.removes.filter(isRecord).filter((change) => isNodeId(change.id) && isNodeId(change.parentId))
        : [];
    // rrweb drops a removed id from its mirror but leaves the DOM node in place when the
    // parent does not match, so only a removal from the tracked parent is refunded.
    forgetRemoved(removes.filter((change) => isNodeId(change.id) && draft.parents.get(change.id) === change.parentId).map((change) => change.id).filter(isNodeId), draft);
    const known = new Set(rawAdds.map((add) => (isRecord(add.node) ? add.node.id : null)).filter(isNodeId).filter((id) => isKnown(id, draft)));
    rawAdds.forEach((add) => registerStyles(add.node, draft));
    const bound = assetBoundIds(rawAdds, draft);
    const adds = rawAdds.flatMap((add) => {
        if (isTracked(add.parentId, draft) || (typeof add.nextId === "number" && add.nextId < 0)) return [];
        // A frame is rebuilt as a placeholder div, so its document has nowhere to go: rrweb
        // throws inserting a document into an element and the whole replay stays blank.
        if (isRecord(add.node) && add.node.type === NODE_DOCUMENT) return [];
        if (isRecord(add.node) && isNodeId(add.node.id) && (known.has(add.node.id) || draft.parents.has(add.node.id))) return [];
        if (isNodeId(add.parentId) && draft.assets.has(add.parentId)) return sanitizeAssetAdd(add, add.parentId, draft, removes);
        if (isRecord(add.node) && isNodeId(add.node.id) && bound.has(add.node.id)) return [];
        const parentId = isNodeId(add.parentId) ? add.parentId : null;
        const node = sanitizeNode(add.node, draft, parentId);
        if (node === null) return [];
        linkTree(node, parentId, draft);
        return [{ parentId: add.parentId, nextId: add.nextId === undefined ? null : add.nextId, node }];
    });
    const texts = Array.isArray(data.texts)
        ? data.texts.filter(isRecord)
            .filter((text) => isTracked(text.id, draft) === false && (isNodeId(text.id) && draft.assets.has(text.id)) === false)
            .map((text) => ({ id: text.id, value: typeof text.value === "string" ? sanitizeCss(text.value) : null }))
        : [];
    const attributes = Array.isArray(data.attributes)
        ? data.attributes.filter(isRecord).map((change) => sanitizeAttributeChange(change, draft, removes)).filter(isKept)
        : [];
    const isPoisoned = (id: unknown): boolean => isNodeId(id) && draft.assets.get(id)?.poisoned === true;
    return {
        source: SOURCE_MUTATION,
        texts,
        attributes: attributes.filter((change) => isPoisoned(change.id) === false),
        removes,
        adds: adds.filter((add) => isRecord(add.node) === false || add.node.isSVG !== true || isPoisoned(add.node.id) === false),
        ...(data.isAttachIframe === true ? { isAttachIframe: true } : {}),
    };
};

// insertRule / adopted sheets parse each rule on its own ⇒ each rule is cleaned whole.
const sanitizeRuleList = (list: unknown): Record<string, unknown>[] =>
    Array.isArray(list)
        ? list.filter(isRecord).flatMap((entry) => {
            if (typeof entry.rule !== "string") return [];
            const rule = sanitizeCss(entry.rule);
            return rule === "" ? [] : [{ ...entry, rule }];
        })
        : [];

const sanitizeIncremental = (data: Record<string, unknown>, draft: TrackerDraft): Record<string, unknown> | null => {
    const source = data.source;
    if (typeof source !== "number") return null;
    if (typeof data.id === "number" && (data.id < 0 || (draft.assets.has(data.id) && !PASSIVE_SOURCES.has(source)))) return null;
    if (source === SOURCE_MUTATION) return sanitizeMutation(data, draft);
    if (source === SOURCE_STYLE_SHEET_RULE) {
        const out: Record<string, unknown> = { ...data, adds: sanitizeRuleList(data.adds) };
        // replace/replaceSync would swap a whole sheet's text: dropped.
        delete out.replace;
        delete out.replaceSync;
        return out;
    }
    if (source === SOURCE_STYLE_DECLARATION) {
        if (isRecord(data.set) === false) return { ...data, set: undefined };
        const value = typeof data.set.value === "string" ? sanitizeCss(data.set.value) : null;
        return { ...data, set: { ...data.set, value } };
    }
    if (source === SOURCE_ADOPTED_STYLE_SHEET) {
        const styles = Array.isArray(data.styles)
            ? data.styles.filter(isRecord).map((style) => ({ ...style, rules: sanitizeRuleList(style.rules) }))
            : undefined;
        return styles === undefined ? { ...data } : { ...data, styles };
    }
    return PASSIVE_SOURCES.has(source) ? { ...data } : null;
};

// D15: the only Custom event the replay accepts is `vb:error`, rebuilt field by field from
// a closed grammar (no bodies, headers, full URLs or stacks). Every other Custom is dropped.
const ERROR_TAG = "vb:error";
const HTTP_METHODS = new Set(["GET", "POST", "PUT", "PATCH", "DELETE"]);
const PATH_TEMPLATE = /^\/[A-Za-z0-9_:/-]{1,120}$/;
const CLIENT_SOURCES = new Set(["error", "unhandledrejection"]);
const MAX_ERROR_MESSAGE = 200;

export type ErrorPayload =
    | { kind: "http"; method: string; pathTemplate: string; status: number; code: string }
    | { kind: "client"; source: string; message: string }
    | { kind: "offline" }
    | { kind: "online" };

export type SessionError = { at: number; description: string };

// A failed request with no response (status 0) is only a network error.
const isHttpStatus = (status: unknown, code: unknown): status is number =>
    typeof status === "number" && Number.isInteger(status) && ((status >= 100 && status <= 599) || (status === 0 && code === "network"));

const errorPayloadOf = (value: unknown): ErrorPayload | null => {
    if (isRecord(value) === false) return null;
    if (value.kind === "http") {
        const { method, pathTemplate, status, code } = value;
        return typeof method === "string" && HTTP_METHODS.has(method)
            && typeof pathTemplate === "string" && PATH_TEMPLATE.test(pathTemplate)
            && isHttpStatus(status, code) && sessionVocabulary.isCode(code)
            ? { kind: "http", method, pathTemplate, status, code }
            : null;
    }
    if (value.kind === "client") {
        const { source, message } = value;
        return typeof source === "string" && CLIENT_SOURCES.has(source)
            && typeof message === "string" && message.length <= MAX_ERROR_MESSAGE && message.includes("<") === false
            ? { kind: "client", source, message }
            : null;
    }
    if (value.kind === "offline") return { kind: "offline" };
    if (value.kind === "online") return { kind: "online" };
    return null;
};

const describeError = (payload: ErrorPayload): string => {
    if (payload.kind === "http") return `${payload.status === 0 ? "Sin respuesta (red)" : payload.status} ${payload.method} ${payload.pathTemplate}`;
    if (payload.kind === "client") return `Error de la app: ${payload.message}`;
    return payload.kind === "offline" ? "Sin conexión" : "Conexión recuperada";
};

// The errors a (sanitized) scene carries, re-validated by the same grammar, in time order.
const errorOf = (event: ReplayEvent): SessionError | null => {
    const data: unknown = event.data;
    if (event.type !== EVENT_CUSTOM || isRecord(data) === false || data.tag !== ERROR_TAG) return null;
    const payload = errorPayloadOf(data.payload);
    return payload === null ? null : { at: event.timestamp, description: describeError(payload) };
};

const errorsOf = (events: ReplayEvent[]): SessionError[] =>
    events.map(errorOf).filter((error): error is SessionError => error !== null).sort((left, right) => left.at - right.at);

const sanitizeEvent = (event: unknown, draft: TrackerDraft): Record<string, unknown> | null => {
    if (isRecord(event) === false || typeof event.timestamp !== "number" || isRecord(event.data) === false) return null;
    const data = event.data;
    if (event.type === EVENT_META) {
        const width = typeof data.width === "number" ? data.width : 0;
        const height = typeof data.height === "number" ? data.height : 0;
        return { type: EVENT_META, timestamp: event.timestamp, data: { href: "about:blank", width, height } };
    }
    if (event.type === EVENT_FULL_SNAPSHOT) {
        // A full snapshot is a new scene: the stylesheets tracked before it are gone.
        draft.nodes.clear();
        draft.texts.clear();
        draft.assets.clear();
        draft.images.clear();
        draft.parents.clear();
        draft.children.clear();
        draft.sceneBytes = 0;
        draft.scenePixels = 0;
        if (fitsTree(data.node) === false) return null;
        registerStyles(data.node, draft);
        const node = sanitizeNode(data.node, draft, null);
        if (node === null) return null;
        linkTree(node, null, draft);
        return { type: EVENT_FULL_SNAPSHOT, timestamp: event.timestamp, data: { node, initialOffset: isRecord(data.initialOffset) ? data.initialOffset : { left: 0, top: 0 } } };
    }
    if (event.type === EVENT_INCREMENTAL) {
        const incremental = sanitizeIncremental(data, draft);
        return incremental === null ? null : { type: EVENT_INCREMENTAL, timestamp: event.timestamp, data: incremental };
    }
    if (event.type === EVENT_CUSTOM) {
        const payload = data.tag === ERROR_TAG ? errorPayloadOf(data.payload) : null;
        return payload === null ? null : { type: EVENT_CUSTOM, timestamp: event.timestamp, data: { tag: ERROR_TAG, payload } };
    }
    return null;
};

// Structural check at the player boundary: only the four event kinds this pass rebuilds.
const isReplayEvent = (value: Record<string, unknown> | null): value is ReplayEvent & Record<string, unknown> =>
    value !== null
    && (value.type === EVENT_FULL_SNAPSHOT || value.type === EVENT_INCREMENTAL || value.type === EVENT_META || value.type === EVENT_CUSTOM)
    && typeof value.timestamp === "number"
    && isRecord(value.data);

// Events are cleaned in stream order against the stylesheets of the scene they extend.
const sanitizeWith = (payload: unknown, tracker: StyleTracker): { events: ReplayEvent[]; tracker: StyleTracker } | null => {
    if (Array.isArray(payload) === false) return null;
    const draft = draftOf(tracker);
    const events = payload.map((event) => sanitizeEvent(event, draft)).filter(isReplayEvent);
    return { events, tracker: draft };
};

// Events are plain JSON (decoded from the wire, then rebuilt by the pass above), so a JSON
// round trip is a complete, independent copy.
const copyOf = (event: ReplayEvent): ReplayEvent | null => {
    const copy: unknown = JSON.parse(JSON.stringify(event));
    return isRecord(copy) && isReplayEvent(copy) ? copy : null;
};

const narrow = (value: unknown): ReplayEvent | null => (isRecord(value) && isReplayEvent(value) ? value : null);

const sanitize = (payload: unknown): ReplayEvent[] | null => {
    const result = sanitizeWith(payload, EMPTY_TRACKER);
    return result === null ? null : result.events;
};

const isFullSnapshot = (event: ReplayEvent): boolean => event.type === EVENT_FULL_SNAPSHOT;
const isMeta = (event: ReplayEvent): boolean => event.type === EVENT_META;

// The newest recorder timestamp in a scene: where live playback starts.
const liveHeadOf = (events: ReplayEvent[]): number =>
    events.reduce((head, event) => (event.timestamp > head ? event.timestamp : head), Number.NEGATIVE_INFINITY);

// Frame-local rules (ours, not the payload's): what a placeholder says in the replay.
export const PLACEHOLDER_STYLE_RULES: string[] = [
    `[${PLACEHOLDER_ATTRIBUTE}]{display:flex;align-items:center;justify-content:center;min-height:24px;background:#eef1f5;color:#445;font:12px sans-serif;border:1px dashed #99a}`,
    `[${PLACEHOLDER_ATTRIBUTE}]::after{content:attr(${PLACEHOLDER_ATTRIBUTE})}`,
];

export const replaySafety = {
    sanitize,
    sanitizeWith,
    emptyTracker: EMPTY_TRACKER,
    sanitizeCss,
    isInertFrame,
    isFullSnapshot,
    isMeta,
    liveHeadOf,
    copyOf,
    narrow,
    errorOf,
    errorsOf,
};
